import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Crosshair, Trophy, Brain, TrendingUp, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ChartReader() {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iv = setInterval(() => {
      const frame = iframeRef.current;
      try {
        const h = frame?.contentDocument?.body?.scrollHeight;
        if (h && Math.abs(frame.clientHeight - h) > 24) frame.style.height = `${h + 40}px`;
      } catch (e) { /* same-origin only */ }
    }, 700);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Link to="/education" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C5A059] text-sm mb-6 transition-colors" data-testid="back-to-education">
            <ArrowLeft size={15} /> Back to Education Hub
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
                <Crosshair className="text-[#C5A059]" size={16} />
                <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">Interactive Training</span>
              </div>
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-3" data-testid="chart-reader-title">
                Chart <span className="text-[#C5A059]">Reader</span>
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl">
                Train your eyes like a professional trader. Read real-looking charts, call the next move,
                and level up through the Trader Path — from first candle to advanced structure.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Brain, title: 'Learn by Doing', desc: 'Every round is a fresh chart — no memorizing screenshots' },
              { icon: TrendingUp, title: 'Real Market Logic', desc: 'Trends, S/R, patterns, volume and structure — like live charts' },
              { icon: Trophy, title: 'Level Up', desc: 'Progress saves automatically as you climb the Trader Path' },
            ].map((f) => (
              <div key={f.title} className="bg-[#111111] border border-white/10 rounded-lg p-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center shrink-0">
                  <f.icon className="text-[#C5A059]" size={16} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{f.title}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-[#C5A059]/25 overflow-hidden bg-black">
            <iframe
              ref={iframeRef}
              src="/tools/chart-reader.html"
              title="DGG Chart Reader"
              className="w-full block"
              style={{ height: 1400, border: 'none' }}
              data-testid="chart-reader-frame"
            />
          </div>

          <p className="text-gray-600 text-xs mt-4 text-center">
            Progress is saved in your browser. Charts are synthetically generated for training — patterns behave like real markets, but no real capital is at risk.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
